import React, { useEffect, useState } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookOpen, Trash2, Sparkles, ChevronDown, PenLine, Check, MessageCircle, Send } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getIcon } from "@/lib/icons";
import { ReadingText } from "@/components/ReadingText";

const promptFor = (card) =>
  card.journal_prompt || `What does "${card.title || card.name}" stir in you today?`;

function Followups({ reading, onUpdated }) {
  const [thread, setThread] = useState(reading.followups || []);
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [streaming, setStreaming] = useState(false);

  const ask = async () => {
    const question = q.trim();
    if (!question || streaming) return;
    setStreaming(true);
    setAnswer("");
    let full = "";
    try {
      const res = await fetch(`${API}/readings/${reading.id}/followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ question }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = chunk.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          try {
            const p = JSON.parse(line.slice(5).trim());
            if (p.t) {
              full += p.t;
              setAnswer((a) => a + p.t);
            }
            if (p.error) toast.error("The follow-up faltered.");
          } catch (err) {
            console.debug("Skipping unparseable SSE chunk", err);
          }
        }
      }
      const newThread = [...thread, { question, answer: full }];
      setThread(newThread);
      setQ("");
      setAnswer("");
      onUpdated && onUpdated({ ...reading, followups: newThread });
    } catch (e) {
      toast.error("Could not ask your follow-up.");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="mt-6 border-t border-[#D9A441]/15 pt-5" data-testid={`followups-${reading.id}`}>
      <div className="mb-4 flex items-center gap-2">
        <MessageCircle className="h-4 w-4 text-[#D9A441]" />
        <h4 className="font-display text-lg font-medium text-[#F8F3E6]">Ask a follow-up</h4>
        <span className="text-xs italic text-[#A3A5B8]">the cards, in conversation</span>
      </div>

      {thread.length > 0 && (
        <div className="mb-4 space-y-3" data-testid={`followup-thread-${reading.id}`}>
          {thread.map((f, i) => (
            <div key={i} className="space-y-2">
              <div className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm border border-[#D9A441]/25 bg-[#D9A441]/10 px-4 py-2.5 text-sm text-[#F8F3E6]">
                {f.question}
              </div>
              <div className="mr-auto max-w-[92%] rounded-2xl rounded-tl-sm border border-[#D9A441]/15 bg-[#0B0C15]/50 px-4 py-3">
                <ReadingText text={f.answer} className="text-base sm:text-base" />
              </div>
            </div>
          ))}
        </div>
      )}

      {streaming && answer && (
        <div className="mb-4 mr-auto max-w-[92%] rounded-2xl rounded-tl-sm border border-[#D9A441]/15 bg-[#0B0C15]/50 px-4 py-3">
          <ReadingText text={answer} streaming className="text-base sm:text-base" />
        </div>
      )}

      <div className="flex items-end gap-2">
        <Textarea
          data-testid={`followup-input-${reading.id}`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              ask();
            }
          }}
          placeholder="e.g. What does the Future card ask of me?"
          maxLength={500}
          className="min-h-[46px] flex-1 resize-none border-[#D9A441]/20 bg-[#0B0C15]/50 text-sm text-[#F8F3E6] placeholder:text-[#A3A5B8]/50 focus-visible:ring-[#D9A441]"
        />
        <button
          onClick={ask}
          disabled={streaming || !q.trim()}
          data-testid={`followup-ask-${reading.id}`}
          className="flex h-[46px] shrink-0 items-center gap-2 rounded-full border border-[#D9A441]/50 bg-[#D9A441]/10 px-5 text-sm font-medium text-[#E5C365] transition-all hover:bg-[#D9A441]/20 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {streaming ? "…" : "Ask"}
        </button>
      </div>
    </div>
  );
}

function Reflections({ reading, onSaved }) {
  const [values, setValues] = useState(() => ({ ...(reading.reflections || {}) }));
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/readings/${reading.id}/reflections`, { reflections: values });
      onSaved(res.data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      toast.success("Reflections saved ✦");
    } catch (e) {
      toast.error("Could not save your reflections.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 border-t border-[#D9A441]/15 pt-5" data-testid={`reflections-${reading.id}`}>
      <div className="mb-4 flex items-center gap-2">
        <PenLine className="h-4 w-4 text-[#D9A441]" />
        <h4 className="font-display text-lg font-medium text-[#F8F3E6]">Your Reflections</h4>
        <span className="text-xs italic text-[#A3A5B8]">private to you</span>
      </div>
      <div className="space-y-4">
        {reading.cards.map((c, i) => (
          <div key={i}>
            <p className="mb-1.5 text-sm text-[#E5C365]">
              <span className="text-[#A3A5B8]">{c.position} · {c.card.title || c.card.name} — </span>
              {promptFor(c.card)}
            </p>
            <Textarea
              data-testid={`reflection-input-${reading.id}-${i}`}
              value={values[i] || ""}
              onChange={(e) => setValues((v) => ({ ...v, [i]: e.target.value }))}
              placeholder="Write your response…"
              maxLength={2000}
              className="min-h-[70px] resize-none border-[#D9A441]/20 bg-[#0B0C15]/50 text-sm text-[#F8F3E6] placeholder:text-[#A3A5B8]/50 focus-visible:ring-[#D9A441]"
            />
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid={`reflection-save-${reading.id}`}
        className="mt-4 flex items-center gap-2 rounded-full border border-[#D9A441]/50 bg-[#D9A441]/10 px-5 py-2.5 text-sm font-medium text-[#E5C365] transition-all hover:bg-[#D9A441]/20 disabled:opacity-60"
      >
        {savedFlash ? <Check className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}
        {savedFlash ? "Saved" : saving ? "Saving…" : "Save reflections"}
      </button>
    </div>
  );
}

export default function Journal() {
  usePageMeta({ title: "Your Reading Journal - The Cardlight", description: "Your saved tarot and oracle readings, reflections and follow-up questions. Visible only to you; delete any reading anytime.", path: "/journal" });
  const { user, loading, login } = useAuth();
  const [readings, setReadings] = useState([]);
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setBusy(false);
      return;
    }
    api
      .get("/readings")
      .then((res) => setReadings(res.data))
      .catch(() => toast.error("Could not load your journal."))
      .finally(() => setBusy(false));
  }, [user, loading]);

  const remove = async (id) => {
    try {
      await api.delete(`/readings/${id}`);
      setReadings((prev) => prev.filter((r) => r.id !== id));
      toast.success("Reading removed.");
    } catch (e) {
      toast.error("Could not remove this reading.");
    }
  };

  const applyUpdate = (updated) =>
    setReadings((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));

  const reflectionCount = (r) => Object.values(r.reflections || {}).filter((v) => v && v.trim()).length;
  const followupCount = (r) => (r.followups || []).length;

  if (loading || busy) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Sparkles className="h-8 w-8 animate-pulse text-[#D9A441]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-6 text-center">
        <BookOpen className="h-10 w-10 text-[#D9A441]" />
        <h1 className="mt-5 font-display text-4xl font-semibold text-[#F8F3E6]">Your Journal</h1>
        <p className="mt-3 text-[#A3A5B8]">
          Sign in to save your readings and revisit the wisdom of past draws.
        </p>
        <button
          onClick={login}
          data-testid="journal-signin-btn"
          className="mt-6 rounded-full border border-[#D9A441]/60 bg-[#D9A441] px-6 py-3 font-medium text-[#0B0C15] transition-all hover:bg-[#E5C365]"
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="ph-no-capture mx-auto max-w-3xl px-5 py-10 sm:px-8" data-testid="journal-page">
      <div className="mb-8">
        <h1 className="font-display text-4xl font-semibold text-[#F8F3E6] sm:text-5xl">
          Reading Journal
        </h1>
        <p className="mt-2 text-[#A3A5B8]">
          {readings.length} saved {readings.length === 1 ? "reading" : "readings"}
        </p>
      </div>

      {readings.length === 0 ? (
        <div className="rounded-2xl border border-[#D9A441]/15 bg-[#131422]/60 p-10 text-center">
          <p className="text-[#A3A5B8]">No readings yet. Draw your first spread to begin.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="journal-list">
          {readings.map((r) => {
            const isOpen = open === r.id;
            return (
              <motion.div
                key={r.id}
                layout
                className="overflow-hidden rounded-2xl border border-[#D9A441]/20 bg-[#131422]/70"
                data-testid={`journal-item-${r.id}`}
              >
                <div className="flex items-start justify-between gap-4 p-5">
                  <button
                    onClick={() => setOpen(isOpen ? null : r.id)}
                    className="flex-1 text-left"
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#A3A5B8]">
                      <span className="text-[#E5C365]">{r.deck.name}</span>
                      <span className="h-1 w-1 rounded-full bg-[#D9A441]/50" />
                      <span>{r.spread.name}</span>
                      <span className="h-1 w-1 rounded-full bg-[#D9A441]/50" />
                      <span>{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="mt-2 font-display text-xl text-[#F8F3E6]">
                      {r.question ? `“${r.question}”` : r.cards.map((c) => c.card.name).join(" · ")}
                    </p>
                    {reflectionCount(r) > 0 && (
                      <span
                        className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#D9A441]/25 bg-[#0B0C15]/50 px-2.5 py-0.5 text-[11px] text-[#E5C365]"
                        data-testid={`journal-reflection-badge-${r.id}`}
                      >
                        <PenLine className="h-3 w-3" />
                        {reflectionCount(r)} reflection{reflectionCount(r) > 1 ? "s" : ""}
                      </span>
                    )}
                    {followupCount(r) > 0 && (
                      <span
                        className="ml-2 mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#D9A441]/25 bg-[#0B0C15]/50 px-2.5 py-0.5 text-[11px] text-[#E5C365]"
                        data-testid={`journal-followup-badge-${r.id}`}
                      >
                        <MessageCircle className="h-3 w-3" />
                        {followupCount(r)} follow-up{followupCount(r) > 1 ? "s" : ""}
                      </span>
                    )}
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => remove(r.id)}
                      data-testid={`journal-delete-${r.id}`}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-[#A3A5B8] transition-colors hover:bg-[#722F37]/30 hover:text-[#F8F3E6]"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setOpen(isOpen ? null : r.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-[#A3A5B8] transition-transform hover:text-[#F8F3E6]"
                      style={{ transform: isOpen ? "rotate(180deg)" : "none" }}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-[#D9A441]/15 px-5 pb-6 pt-4">
                    <div className="mb-5 flex flex-wrap gap-3">
                      {r.cards.map((c, i) => {
                        const Icon = getIcon(c.card.icon);
                        return (
                          <div
                            key={i}
                            className="flex items-center gap-2 rounded-lg border border-[#D9A441]/20 bg-[#0B0C15]/50 px-3 py-2"
                          >
                            <Icon className="h-4 w-4 text-[#D9A441]" />
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-[#A3A5B8]">
                                {c.position}
                              </p>
                              <p className="text-sm text-[#F8F3E6]">
                                {c.card.name}
                                {c.reversed && (
                                  <span className="ml-1 text-xs text-[#E5C365]">(rev)</span>
                                )}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <ReadingText text={r.interpretation} />
                    <Reflections reading={r} onSaved={applyUpdate} />
                    <Followups reading={r} onUpdated={applyUpdate} />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
